import {
  useCreateDemoDraftMutation,
  useGetCurrentUserQuery,
  useGetDraftQuery,
  useGetDraftsQuery,
  useGetOrganizationQuery,
  useLazyGetModificationsQuery,
} from '@carboncopyco/data';
import { EditorSdkProvider } from '@carboncopyco/editor-sdk';
import { RecordingTypes } from '@carboncopyco/types';
import type { IGlobalTab } from '@carboncopyco/types/desktop';
import { useEffect, useState } from 'react';
import { settings } from '../../../settings';
import { EditorContainer } from '../components/EditorContainer';
import useSDKCommunicationLayer from '../hooks/useSDKCommunicationLayer';
import rendererService from '../services/RendererService';
import { useTabManager } from '../services/TabManager';
import debuggerInterceptorPathListState from '../shared/utils/debugger-interceptor-path-list-state';
import { useUser } from '../store/context/UserContext';
import { useEditorApi } from './utils/useEditorApi';

export const StartEditingScreen = ({
  globalTab,
  demoId,
}: {
  globalTab: IGlobalTab;
  demoId: number;
}) => {
  const [isDraftLoadedOnce, setIsDraftLoadedOnce] = useState(false);
  const { closeGlobalTab, updateGlobalTabPayload } = useTabManager();
  const [createDraft] = useCreateDemoDraftMutation();
  const [draftModifiedHandled, setDraftModifiedHandled] = useState(false);
  const { activeOrganization } = useUser();
  const communicationLayer = useSDKCommunicationLayer(globalTab.id);

  const { data: organization } = useGetOrganizationQuery(
    { id: activeOrganization?.id?.toString() },
    { skip: !activeOrganization?.id }
  );

  const { data: userData } = useGetCurrentUserQuery();
  const user = userData?.user;

  const { data: drafts, isFetching } = useGetDraftsQuery(
    {
      organizationId: activeOrganization?.id?.toString(),
      sourceType: 'demo',
      sourceId: Number(demoId),
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );
  const draftId = drafts?.[0]?.id;
  const { data: draft } = useGetDraftQuery(
    {
      organizationId: activeOrganization?.id?.toString(),
      draftId: String(draftId ?? ''),
    },
    { skip: !draftId }
  );

  const [fetchModifications, { data: modifications }] =
    useLazyGetModificationsQuery();

  const getModifications = () =>
    fetchModifications({
      organizationId: activeOrganization?.id?.toString(),
      entityType: 'draft',
      entityId: String(draft?.id ?? ''),
      apiVersion: 2,
    }).unwrap();

  useEffect(() => {
    if (!draft?.id) {
      return;
    }

    // eslint-disable-next-line no-console
    getModifications().catch(console.error);
  }, [draft?.id]);

  const api = useEditorApi({
    organizationId: activeOrganization?.id?.toString(),
    demoId: String(demoId),
    draftId: String(draft?.id),
    tab: globalTab,
    getModifications,
  });

  const { updateImageReplacePathListStateModification } =
    debuggerInterceptorPathListState(globalTab);

  useEffect(() => {
    if (draft && modifications && !isDraftLoadedOnce) {
      updateImageReplacePathListStateModification(modifications);
      const updatedPayload = {
        ...globalTab.payload,
        modifications,
        draftPlayerToken: draft.playerToken,
        draftId: draft.id,
      };
      updateGlobalTabPayload(globalTab.id, updatedPayload);
      rendererService.createGlobalTab({
        ...globalTab,
        payload: updatedPayload,
      });
      setIsDraftLoadedOnce(true);
    }
  }, [draft, modifications]);

  const onExit = () => {
    window.dsDesktop.editor.exitEditor(globalTab);
    closeGlobalTab(globalTab?.id);
  };

  useEffect(() => {
    if (drafts && !drafts.length) {
      createDraft({
        organizationId: activeOrganization?.id?.toString(),
        id: String(demoId),
      });
    }
  }, [drafts, demoId]);

  useEffect(() => {
    if (!draftModifiedHandled && draft?.modified) {
      setDraftModifiedHandled(true);
      updateGlobalTabPayload(globalTab.id, {
        draftModified: true,
      });
    }

    if (!draft) {
      setDraftModifiedHandled(false);
    }
  }, [draft]);

  if (!organization || !user) {
    return null;
  }

  return (
    <EditorSdkProvider
      editorSdkId={String(globalTab.id)}
      communicationLayer={communicationLayer}
      mode="editor"
      organizationData={organization}
      userData={user}
      api={api}
      draft={draft}
      modifications={modifications}
      filesServiceDomain={settings.FILES_SERVICE_URL ?? ''}
      recordingType={
        (globalTab.payload.demo.recordingType as RecordingTypes) ===
        RecordingTypes.overlay
          ? 'overlay'
          : 'cloner'
      }
    >
      <div className="flex flex-col flex-1">
        <EditorContainer
          organization={organization}
          draft={draft}
          isFetchingDraft={isFetching || draft?.id !== draftId}
          tab={globalTab}
          onExit={onExit}
        />
      </div>
    </EditorSdkProvider>
  );
};
